import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["search", "selection", "results", "status", "selectedList"]
  static values = { url: String, multiple: Boolean, labels: Object, messages: Object }

  label(key) { return this.labelsValue[key] || "" }

  message(key, values) {
    return Object.entries(values).reduce((text, [name, value]) => text.replace(`%{${name}}`, value), this.messagesValue[key] || "")
  }

  connect() {
    this.selected = new Map()
  }

  disconnect() {
    clearTimeout(this.timer)
    this.request?.abort()
  }

  search() {
    if (!this.multipleValue && this.hasSelectionTarget) this.selectionTarget.value = ""
    this.searchTarget.setCustomValidity(this.multipleValue || !this.searchTarget.value.trim() ? "" : this.label("select_a_member_from_the_search_results"))
    clearTimeout(this.timer)
    this.request?.abort()
    this.resultsTarget.replaceChildren()
    this.timer = setTimeout(() => this.load(), 200)
  }

  open() {
    if (this.multipleValue || !this.hasSelectionTarget || !this.selectionTarget.value) this.load()
  }

  async load() {
    this.request?.abort()
    const request = new AbortController()
    this.request = request
    this.statusTarget.textContent = this.label("searching_approved_members")
    this.resultsTarget.hidden = true
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", this.searchTarget.value.trim())
      const response = await fetch(url, { signal: request.signal, headers: { Accept: "application/json" }, cache: "no-store" })
      if (!response.ok || response.redirected) throw new Error("Search unavailable")
      const data = await response.json()
      if (request.signal.aborted) return
      this.resultsTarget.replaceChildren()
      for (const member of data.members) {
        const button = document.createElement("button")
        button.type = "button"
        button.className = "block w-full text-left rounded-lg px-3 py-2 text-base"
        button.textContent = `${member.name} — ${member.email || this.label("no_email")}`
        button.addEventListener("click", () => {
          if (this.multipleValue) {
            if (!this.selected.has(member.id)) this.selected.set(member.id, member)
            this.renderSelected()
            this.searchTarget.value = ""
            this.searchTarget.setCustomValidity("")
            this.close()
            this.statusTarget.textContent = ""
          } else {
            this.selectionTarget.value = member.id
            this.searchTarget.value = button.textContent
            this.searchTarget.setCustomValidity("")
            this.close()
            this.statusTarget.textContent = this.message("ui_selected", { name: member.name })
          }
          this.searchTarget.focus()
        })
        this.resultsTarget.append(button)
      }
      this.resultsTarget.hidden = data.members.length === 0
      this.statusTarget.textContent = data.members.length === 0
        ? this.label("no_approved_members_found_try_another_name_or_email")
        : data.has_more ? this.label("showing_the_first_20_matches_refine_your_search_to_find_another_member") : this.label("choose_a_member_from_the_results")
    } catch (error) {
      if (error.name !== "AbortError") this.statusTarget.textContent = this.label("could_not_load_approved_members_focus_the_search_field_to_retry")
    }
  }

  renderSelected() {
    if (!this.hasSelectedListTarget) return
    this.selectedListTarget.replaceChildren()
    for (const [id, member] of this.selected) {
      const row = document.createElement("div")
      row.className = "reference-selected-admin"
      const label = document.createElement("span")
      label.textContent = `${member.name} — ${member.email || this.label("no_email")}`
      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "reference-selected-admin__remove"
      remove.textContent = this.label("remove")
      remove.addEventListener("click", () => { this.selected.delete(id); this.renderSelected(); this.searchTarget.focus() })
      const hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = "administrator_ids[]"
      hidden.value = id
      row.append(label, remove, hidden)
      this.selectedListTarget.append(row)
    }
  }

  close() {
    clearTimeout(this.timer)
    this.request?.abort()
    this.resultsTarget.hidden = true
  }

  outside(event) {
    if (!this.element.contains(event.target)) this.close()
  }
}
